import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import {
  SaveFerryingPeriodRequest,
  SaveVesselJourneyRequest,
  SaveVesselPortCallRequest,
  SaveVesselLookupRequest,
  SaveVesselPortRequest,
  SaveVesselRequest,
} from '@/types';
import { QUERY_KEYS } from '../../queryKeys';
import { vessel } from '../Vessel';

const refreshCatalogue = () => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselPositions] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselJourneys] });
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselPortCalls] });
};

export const useVessels = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.vessels],
    queryFn: async () => (await vessel.getVessels()).data,
    enabled,
  });

export const useVesselPorts = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.vesselPorts],
    queryFn: async () => (await vessel.getVesselPorts()).data,
    enabled,
  });

export const useVesselJourneys = (vesselId?: number, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.vesselJourneys, vesselId ?? 'all'],
    queryFn: async () => (await vessel.getVesselJourneys(vesselId)).data,
    enabled,
  });

export const useSaveVessel = () =>
  useMutation({
    mutationFn: (body: SaveVesselRequest) => vessel.saveVessel(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vessels] });
      refreshCatalogue();
    },
  });

export const useSaveVesselPort = () =>
  useMutation({
    mutationFn: (body: SaveVesselPortRequest) => vessel.saveVesselPort(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselPorts] });
    },
  });

export const useVesselAgents = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.vesselAgents],
    queryFn: async () => (await vessel.getVesselAgents()).data,
    enabled,
  });

export const useSaveVesselAgent = () =>
  useMutation({
    mutationFn: (body: SaveVesselLookupRequest) => vessel.saveVesselAgent(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselAgents] });
    },
  });

export const useVesselBerths = (enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.vesselBerths],
    queryFn: async () => (await vessel.getVesselBerths()).data,
    enabled,
  });

export const useSaveVesselBerth = () =>
  useMutation({
    mutationFn: (body: SaveVesselLookupRequest) => vessel.saveVesselBerth(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.vesselBerths] });
    },
  });

export const usePortCalls = (
  status?: string,
  enabled = true,
  filters?: {
    siteId?: number;
    vesselId?: number;
    etaFrom?: string;
    etaTo?: string;
    shippingReference?: string;
  },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.vesselPortCalls, status ?? 'all', filters ?? {}],
    queryFn: async () =>
      (
        await vessel.getPortCalls({
          status,
          ...filters,
        })
      ).data,
    enabled,
  });

export const useSavePortCall = () =>
  useMutation({
    mutationFn: (body: SaveVesselPortCallRequest) => vessel.savePortCall(body),
    onSuccess: refreshCatalogue,
  });

export const useSaveFerryingPeriod = () =>
  useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: SaveFerryingPeriodRequest;
    }) => vessel.saveFerryingPeriod(id, body),
    onSuccess: refreshCatalogue,
  });

export const useDeleteFerryingPeriod = () =>
  useMutation({
    mutationFn: ({ id, periodId }: { id: number; periodId: number }) =>
      vessel.deleteFerryingPeriod(id, periodId),
    onSuccess: refreshCatalogue,
  });

export const useCompleteReadiness = () =>
  useMutation({
    mutationFn: ({
      id,
      checkId,
      complete,
    }: {
      id: number;
      checkId: number;
      complete: boolean;
    }) => vessel.completeReadiness(id, checkId, complete),
    onSuccess: refreshCatalogue,
  });

export const useStartDischarge = () =>
  useMutation({
    mutationFn: (id: number) => vessel.startDischarge(id),
    onSuccess: refreshCatalogue,
  });

export const useClosePortCall = () =>
  useMutation({
    mutationFn: (id: number) => vessel.closePortCall(id),
    onSuccess: refreshCatalogue,
  });

export const useSaveVesselJourney = () =>
  useMutation({
    mutationFn: (body: SaveVesselJourneyRequest) =>
      vessel.saveVesselJourney(body),
    onSuccess: refreshCatalogue,
  });
